import type { CarCardData, SearchParams, SearchResult } from "@/lib/api";
import { carsInCountry, normalizeInventoryCountry } from "./country-inventory";

const TARGET_COUNT = 8;

export interface CategoryBrowserStageTiming {
  stage: "local" | "country" | "featured";
  durationMs: number;
  status: "success" | "empty" | "partial" | "failed";
}

export interface CategoryBrowserResult {
  cars: CarCardData[];
  /** A worldwide homepage fallback, never inventory in the detected country. */
  worldwideFallback?: boolean;
  fallbackCountry?: string;
  countAfterCity: number;
  countAfterCategory: number;
  /** The last tier that contributed a visible car to this result. */
  renderedStage: "local" | "country" | "featured" | "empty";
  /** Request-stage evidence for diagnosing slow or unavailable catalogue tiers. */
  stageTimings: CategoryBrowserStageTiming[];
}

type SearchCars = (params: SearchParams) => Promise<SearchResult>;
type GetFeaturedCars = (limit: number, countryCode?: string) => Promise<{ cars: CarCardData[] }>;

/**
 * Run the homepage category waterfall without allowing one failed request to
 * discard successful results from a broader tier.
 */
export async function loadCategoryBrowserCars(
  activeSlug: string,
  lat: number | null | undefined,
  lng: number | null | undefined,
  countryCode: string | undefined,
  searchCars: SearchCars,
  getFeaturedCars: GetFeaturedCars,
  options: { allowWorldwideFallback?: boolean; fallbackMarkets?: readonly string[] } = {},
): Promise<CategoryBrowserResult> {
  const country = normalizeInventoryCountry(countryCode);
  const cars: CarCardData[] = [];
  const seen = new Set<number>();
  const stageTimings: CategoryBrowserStageTiming[] = [];
  let lastError: unknown = null;
  let renderedStage: CategoryBrowserResult["renderedStage"] = "empty";
  let worldwideFallback = false;
  let fallbackCountry: string | undefined;

  const pushUnique = (
    list: CarCardData[] | undefined,
    stage: Exclude<CategoryBrowserResult["renderedStage"], "empty">,
    scope: string | null = country ?? null,
    classicsOnly = true,
  ) => {
    if (!list) return;
    let added = false;
    for (const car of carsInCountry(list, scope)) {
      if (cars.length >= TARGET_COUNT) break;
      if (classicsOnly && car.year && car.year > 1995) continue;
      if (seen.has(car.id)) continue;
      seen.add(car.id);
      cars.push(car);
      added = true;
    }
    if (added) renderedStage = stage;
  };
  const filled = () => cars.length >= TARGET_COUNT;

  const runStage = async (
    stage: CategoryBrowserStageTiming["stage"],
    requests: Array<Promise<SearchResult | { cars: CarCardData[] }>>,
    onSuccess: (
      result: SearchResult | { cars: CarCardData[] },
      index: number,
    ) => void,
  ) => {
    const startedAt = Date.now();
    const settled = await Promise.allSettled(requests);
    const before = cars.length;
    let fulfilled = 0;
    settled.forEach((outcome, index) => {
      if (outcome.status === "fulfilled") {
        fulfilled += 1;
        onSuccess(outcome.value, index);
      } else if (lastError === null) {
        lastError = outcome.reason;
      }
    });
    const rejected = settled.length - fulfilled;
    stageTimings.push({
      stage,
      durationMs: Math.max(0, Date.now() - startedAt),
      status: rejected === settled.length
        ? "failed"
        : cars.length === before
          ? "empty"
          : rejected > 0
            ? "partial"
            : "success",
    });
  };

  const hasGeo = lat != null && lng != null;
  const carTypeFilter = activeSlug ? [activeSlug] : undefined;
  const pageSize = TARGET_COUNT * 2;
  const boundsAround = (radius: number) => ({
    bounds_north: (lat as number) + radius,
    bounds_south: (lat as number) - radius,
    bounds_east: (lng as number) + radius,
    bounds_west: (lng as number) - radius,
  });

  let countAfterCity = 0;
  if (hasGeo) {
    await runStage(
      "local",
      [
        searchCars({ car_type: carTypeFilter, country_code: country, ...boundsAround(1.5), pageSize, sort: "score" }),
        searchCars({ car_type: carTypeFilter, country_code: country, ...boundsAround(5), pageSize, sort: "score" }),
      ],
      (result, index) => {
        pushUnique(result.cars, "local");
        if (index === 0) countAfterCity = cars.length;
      },
    );
  }

  if (!filled()) {
    await runStage(
      "country",
      [
        searchCars({ car_type: carTypeFilter, country_code: country, pageSize, sort: "score" }),
        ...(activeSlug && !country
          ? [searchCars({ car_type: carTypeFilter, pageSize, sort: "score" })]
          : []),
      ],
      result => pushUnique(result.cars, "country"),
    );
  }
  const countAfterCategory = cars.length;

  if (!filled()) {
    await runStage(
      "featured",
      [
        ...(carTypeFilter
           ? [searchCars({ country_code: country, pageSize, sort: "score" })]
          : []),
        country ? getFeaturedCars(pageSize, country) : getFeaturedCars(pageSize),
      ],
      result => pushUnique(result.cars, "featured"),
    );
  }

  // Detected geography is a preference on the worldwide homepage, not a
  // reason to leave discovery empty. Explicit country pages remain strict.
  if (cars.length === 0 && options.allowWorldwideFallback) {
    // Try the closest other supported markets before the worldwide safety
    // net. Bound the number of empty-market reads on each page load.
    const markets = [...new Set(options.fallbackMarkets ?? [])]
      .filter(code => code !== country).slice(0, 3);
    for (const market of markets) {
      await runStage("country", [
        searchCars({ car_type: carTypeFilter, country_code: market, pageSize, sort: "score" }),
        getFeaturedCars(pageSize, market),
      ], result => pushUnique(result.cars, "country", market));
      if (cars.length > 0) {
        fallbackCountry = market;
        break;
      }
    }
  }

  if (cars.length === 0 && options.allowWorldwideFallback) {
    if (activeSlug) {
      await runStage("country", [
        searchCars({ car_type: carTypeFilter, pageSize, sort: "score" }),
      ], result => pushUnique(result.cars, "country", null));
    }
    if (!filled()) {
      const alternatives: CarCardData[] = [];
      await runStage("featured", [
        searchCars({ pageSize, sort: "score" }),
        getFeaturedCars(pageSize),
      ], result => {
        alternatives.push(...result.cars);
        pushUnique(result.cars, "featured", null);
      });
      // Real bookable cars still beat an empty shelf if the returned stock
      // contains only newer vehicles. Never fabricate listings.
      if (cars.length === 0) pushUnique(alternatives, "featured", null, false);
    }
    worldwideFallback = country != null && cars.length > 0;
  }

  if (cars.length === 0 && lastError !== null) throw lastError;
  return {
    cars,
    worldwideFallback,
    fallbackCountry,
    countAfterCity,
    countAfterCategory,
    renderedStage,
    stageTimings,
  };
}